<script setup lang="ts" generic="T">
import { QItem, QSelect, QVirtualScroll } from 'quasar'
import { computed, onMounted, ref, useSlots, watch } from 'vue'
import type { Ref } from 'vue'
import { Color } from '@previo/codebooks'
import { $t } from '../../composables/i18n'
import type { PValidationRule } from '../../types/types'
import { useHandleScrollQSelect } from '../../composables/menu/handleScrollQSelect'
import { useMenuMaxHeight } from '../../composables/menu/useMenuMaxHeight'
import { PMaxLines } from '../typography/types'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import { usePrevioDS } from '../../composables/helpers'
import { menuOffset } from './composable/menuOffset'
import InputFieldLabel from './components/LabelInputField.vue'
import MobileSuggestionDialog from './components/MobileSuggestionDialog.vue'
import Chip from './components/Chip.vue'
import PCheckbox from './PCheckbox.vue'
import PTextInputField from './PTextInputField.vue'
import { PInputAutofocusType } from './types'

type Props = {
  label?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  tooltip?: null | string
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  disableClearable?: boolean
  autofocus?: boolean
  autofocusType?: null | PInputAutofocusType
  errorMessage?: null | string
  items: T[] | []
  itemValue: keyof T
  itemText: ((item: T) => string) | keyof T
  noData?: string
  cy: string
}

const model = defineModel<(number | string | null)[]>({ default: [] })

const props = withDefaults(defineProps<Props>(), {
  disable: false,
  readonly: false,
  debounce: 0,
  disableClearable: false,
  autofocus: false,
  autofocusType: PInputAutofocusType.INPUT,
  rules() {
    return []
  },
})

defineEmits(['blur'])

const slots = useSlots()
const ds = usePrevioDS()

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const handleScrollQSelect = useHandleScrollQSelect(qSelectComponent)
const menuMaxHeight = useMenuMaxHeight(() => qSelectComponent.value?.$el as HTMLElement | undefined)

const mobileInputValue = ref<null | string>(null)
const isMobileDialogShow = ref<boolean>(false)

// Hack: Quasar emit `@popup-show` on QSelect after call `removeAtIndex` function in `#selected-item` slot. This is solution how to prevent it
const isOpenMobileDialogIgnored = ref<boolean>(false)

const itemsFiltered = ref(props.items) as Ref<T[]>

watch(
  () => props.items,
  (val) => {
    itemsFiltered.value = val
    qSelectComponent.value?.filter('')
  },
)

watch(model, () => {
  qSelectComponent.value?.updateInputValue('')
})

const isRequired = computed(() => {
  return props.rules.includes(ds.rules.required)
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})

const filter = (val: null | string, update: (callback: () => void) => void): void => {
  if (val === null) {
    itemsFiltered.value = props.items

    update(() => {})

    return
  }

  const needle = val.toLowerCase()
  itemsFiltered.value = props.items.filter(
    (item: T) => itemLabel(item)?.toLowerCase().indexOf(needle) > -1,
  )

  update(() => {})
}

const isSelected = (item: T): boolean => {
  return model.value.includes(item[props.itemValue] as string | number | null)
}

const selectItem = (item: T): void => {
  qSelectComponent.value?.toggleOption(item[props.itemValue])
  qSelectComponent.value?.updateInputValue('')
  mobileInputValue.value = null
}

const itemLabel = (item: T): string => {
  return typeof props.itemText === 'function'
    ? props.itemText(item)
    : (item[props.itemText] as string)
}

const isModelEmpty = computed<boolean>(() => {
  return model.value.length === 0
})

const clear = (): void => {
  model.value = []
}

const removeChip = (index: number, removeAtIndex: (index: number) => void): void => {
  if (props.readonly) {
    return
  }

  isOpenMobileDialogIgnored.value = true
  removeAtIndex(index)
}

const onMenuShow = (): void => {
  if (ds.screen().isLaptopAndLarge) {
    handleScrollQSelect.subscribe()
    menuMaxHeight.subscribe()

    return
  }

  qSelectComponent.value?.hidePopup()

  if (isOpenMobileDialogIgnored.value) {
    isOpenMobileDialogIgnored.value = false
  } else {
    isMobileDialogShow.value = true
  }
}

const onMenuHide = (): void => {
  if (ds.screen().isLaptopAndLarge) {
    handleScrollQSelect.unsubscribe()
    menuMaxHeight.unsubscribe()
  }
}

watch(mobileInputValue, (val: string | null) => {
  filter(val, () => {})
})

const showPopup = (): void => {
  if (ds.screen().isMobile) {
    isMobileDialogShow.value = true

    return
  }

  qSelectComponent.value?.showPopup()
}

onMounted(() => {
  if (props.autofocus && props.autofocusType === PInputAutofocusType.DROPDOWN) {
    showPopup()
  }
})
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-select
    ref="qSelectComponent"
    v-model="model"
    :rules="rules"
    lazy-rules
    :placeholder="ds.screen().isLaptopAndLarge && isModelEmpty ? placeholder : undefined"
    :hint="hint ?? undefined"
    :options="itemsFiltered"
    :option-value="itemValue as string"
    :error="hasAnyErrors"
    :error-message="errorMessage ?? undefined"
    :autofocus="autofocus"
    dropdown-icon="fa-light fa-chevron-down"
    :bg-color="Color.WHITE"
    behavior="menu"
    :menu-offset="menuOffset"
    popup-no-route-dismiss
    hide-bottom-space
    map-options
    outlined
    no-error-icon
    emit-value
    :use-input="ds.screen().isLaptopAndLarge"
    use-chips
    multiple
    autocomplete="nope"
    clear-icon="fa-light fa-xmark"
    class="p-multi-select-input-field"
    :popup-content-class="menuMaxHeight.popupContentClass.value"
    :popup-content-style="menuMaxHeight.popupContentStyle.value"
    :clearable="!disableClearable"
    :disable="disable"
    :readonly="readonly"
    :class="{ 'ds-input-field--readonly': readonly }"
    :input-debounce="debounce ?? 0"
    :data-cy="cy"
    @blur="$emit('blur')"
    @filter="filter"
    @clear="clear"
    @popup-show="onMenuShow"
    @popup-hide="onMenuHide"
  >
    <template v-if="ds.screen().isMobile && isModelEmpty" #prepend>
      <p-text-form-body :text="placeholder" :color="Color.GREY_400" :max-lines="PMaxLines.ONE" />
    </template>

    <template
      #selected-item="{
        index,
        opt,
        removeAtIndex,
      }: {
        index: number
        opt: T
        removeAtIndex(index: number): void
      }"
    >
      <slot
        name="selected-item-chips"
        :item="opt"
        :readonly="readonly"
        :remove-item="
          () => {
            removeChip(index, removeAtIndex)
          }
        "
      >
        <chip
          :label="itemLabel(opt)"
          :hide-remove="readonly"
          @remove="
            () => {
              removeChip(index, removeAtIndex)
            }
          "
        />
      </slot>
    </template>

    <template
      #option="{
        itemProps,
        opt,
        selected,
        toggleOption,
      }: {
        itemProps: object
        opt: T
        selected: boolean
        toggleOption(opt: T): void
      }"
    >
      <q-item v-bind="itemProps" class="p-items-center">
        <p-checkbox
          :model-value="selected"
          :cy="`item_${opt[itemValue as keyof T]}`"
          @update:model-value="toggleOption(opt)"
        />
        <template v-if="!!slots['item']">
          <slot name="item" :item="opt" :is-selected="selected" />
        </template>

        <template v-else>
          <p-text-form-body
            :text="itemLabel(opt)"
            :color="selected ? Color.PRIMARY : Color.GREY_600"
            :max-lines="PMaxLines.ONE"
          />
        </template>
      </q-item>
    </template>
    <template v-if="slots['options-header']" #before-options>
      <slot name="options-header" />
    </template>
    <template #no-option>
      <q-item>
        <div class="p-flex p-flex-center">
          <p-text-form-body :text="noData || $t('No items match the filter')" />
        </div>
      </q-item>
    </template>
  </q-select>

  <mobile-suggestion-dialog v-model="isMobileDialogShow" show-confirm-buttons>
    <template #input>
      <p-text-input-field
        v-model="mobileInputValue"
        end-icon="magnifying-glass"
        :placeholder="$t('Search')"
        :debounce="debounce ?? 0"
        autofocus
        cy="search-input"
      />
    </template>
    <template #default>
      <slot name="options-header" />
      <q-virtual-scroll :items="itemsFiltered" separator class="p-flex-1">
        <template #default="{ item }">
          <q-item clickable @click="selectItem(item)">
            <div class="p-flex p-items-center p-flex-1">
              <p-checkbox
                :model-value="isSelected(item)"
                :cy="`item-${item[itemValue]}`"
                @update:model-value="selectItem(item)"
              />

              <template v-if="!!slots['item']">
                <slot name="item" :item="item" :is-selected="isSelected(item)" />
              </template>

              <template v-else>
                <p-text-form-body
                  :text="itemLabel(item)"
                  :color="isSelected(item) ? Color.PRIMARY : Color.GREY_600"
                  :max-lines="PMaxLines.ONE"
                />
              </template>
            </div>
          </q-item>
        </template>
      </q-virtual-scroll>
    </template>
  </mobile-suggestion-dialog>
</template>
